"use client";

// Spanish copy of components/widgetCatalog for /es/platforms/<slug> (DEV-15362,
// ES batch 2). Same layout, filters and search. Every card arrives with its
// link already resolved on the server (the Spanish page when one exists, the
// English one otherwise, marked EN), Spanish names where the widget has a
// Spanish page and a Spanish one-liner for every widget. Search also matches
// the English name, so "Google Reviews" still finds its card.
import React, { Suspense, useCallback, useRef, useState } from "react";
import { StyledDiv } from "../widgetCatalog/widgetCatalog.styles";
import { EnTag } from "./langSwitch.comp";
import { IoMdClose } from "@react-icons/all-files/io/IoMdClose";
import { AiFillStar } from "@react-icons/all-files/ai/AiFillStar";
import { AiFillFire } from "@react-icons/all-files/ai/AiFillFire";
import { RiLeafFill } from "@react-icons/all-files/ri/RiLeafFill";
import Image from "next/image";
import Link from "next/link";

import type {
  ICatalogApp,
  ICatalogTaxonomy,
  IPlatformPageData,
} from "../../helpers/catalogData";
import { Button } from "../button/button.comp";

// Home-view section names (DB tags), shown in Spanish. The English names stay
// in the data because the section icons key on them.
const TAG_ES: Record<string, string> = {
  Popular: "Populares",
  Trending: "Tendencias",
  Reviews: "Reseñas",
};

export type ICatalogAppEs = ICatalogApp & { href: string; enName: string; isEn: boolean };
import { useRouter, useSearchParams } from "next/navigation";
import { debounce } from "lodash";

interface IWidgetCatalogProps {
  apps: ICatalogAppEs[];
  platform: Pick<IPlatformPageData, "slug" | "title">;
  hideHome?: boolean;
  categories: ICatalogTaxonomy[];
  tags: ICatalogTaxonomy[];
}

export const WidgetCatalogEs = ({
  apps,
  platform,
  hideHome = false,
  categories,
  tags,
}: IWidgetCatalogProps) => {
  return (
    <StyledDiv>
      <Suspense>
        <Catalog
          apps={apps}
          platform={platform}
          hideHome={hideHome}
          categories={categories}
          tags={tags}
        />
      </Suspense>
    </StyledDiv>
  );
};

function Catalog({
  apps,
  platform,
  hideHome = false,
  categories,
  tags,
}: IWidgetCatalogProps) {
  const searchParams = useSearchParams();
  const searchQuery = searchParams?.get("search") || "";
  const categoryQuery = searchParams?.get("category") || "";
  const router = useRouter();
  const listRef = useRef<any>();
  const [filter, setFilter] = useState({
    page: "",
    name: searchQuery || "",
    tag: "",
    cat: categoryQuery || "",
  });
  const pluginList = apps.sort((a, b) => a.order - b.order);
  const searchText = filter.name?.toLowerCase() || "";

  const tagList = pluginList.reduce((acc: string[], plugin: ICatalogAppEs) => {
    (plugin.newTags || []).forEach((tag) => {
      const wantedTag = tags.find((t) => t._id === tag._id);
      if (wantedTag && !acc.includes(wantedTag.name)) {
        acc.push(wantedTag.name);
      }
    });
    return acc;
  }, []);

  const catList = pluginList.reduce((acc: any, plugin: ICatalogAppEs) => {
    (plugin.newCategories || []).forEach((category) => {
      const wantedCat = categories.find((c) => c._id === category._id);

      if (wantedCat && !acc[wantedCat.name]) acc[wantedCat.name] = 1;
      else acc[wantedCat?.name as string] = acc[wantedCat?.name as string] + 1;
    });
    return acc;
  }, {});

  const weightedSearch = () => {
    return pluginList
      .map((plugin) => {
        let score = 0;

        // Assign scores based on match
        if (
          plugin.name?.toLowerCase().includes(searchText) ||
          plugin.enName?.toLowerCase().includes(searchText)
        ) {
          score += 3; // Higher weight for name matches
        }
        if (plugin.teaser?.toLowerCase().includes(searchText)) {
          score += 2; // Medium weight for teaser matches
        }
        if (plugin.description?.toLowerCase().includes(searchText)) {
          score += 1; // Lower weight for description matches
        }
        if (
          plugin.newCategories?.some(
            (c) => c.name?.toLowerCase() === searchText?.toLowerCase()
          )
        ) {
          score += 3;
        }

        return { plugin, score };
      })
      .filter(({ score }) => score > 0) // Filter out plugins with no match
      .sort((a, b) => b.score - a.score) // Sort by score in descending order
      .map(({ plugin }) => plugin); // Return the sorted plugins
  };

  const filteredPlugins = weightedSearch()
    .filter((p) =>
      filter.tag ? (p.newTags || []).map((t) => t.name).includes(filter.tag) : p
    )
    .filter((p) =>
      filter.cat
        ? (p.newCategories || [])
            .map((t) => t.name?.toLowerCase())
            .includes(filter.cat.toLowerCase())
        : p
    );

  const debouncedSearch = useCallback(
    debounce((val: string) => {
      const baseUrl = `/es/platforms/${platform.slug}`;
      const url = `${baseUrl}?search=${encodeURIComponent(val)}`;
      router.replace(url);
    }, 300), // 300ms delay
    [router]
  );

  function handleSearchChange(value: string) {
    setFilter((prevFilter) => ({
      ...prevFilter,
      name: value,
    }));
    debouncedSearch(value);
  }

  function renderSidebar() {
    return (
      <div className="filter-container">
        <form
          className="search-components"
          onSubmit={(e) => e.preventDefault()}
        >
          <input
            name="searchType"
            title="Buscar widgets"
            placeholder="Buscar widgets..."
            value={filter.name}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {filter.name && (
            <IoMdClose
              onClick={() => handleSearchChange("")}
              className="reset"
              fontSize={20}
            />
          )}
        </form>
        <div className="categories">
          {!hideHome && (
            <>
              <button
                className={`cat ${filter.page === "home" ? "active" : ""}`}
                onClick={() =>
                  setFilter({
                    ...filter,
                    cat: "",
                    tag: "",
                    name: "",
                    page: "home",
                  })
                }
              >
                Inicio
              </button>
              <div className="divider"></div>
            </>
          )}
          <p className="cat-title">Categorías</p>
          <button
            className={`cat ${
              !filter.page && !filter.tag && !filter.cat ? "active" : ""
            }`}
            onClick={() =>
              setFilter({ ...filter, page: "", cat: "", tag: "", name: "" })
            }
          >
            Todos ({pluginList.length})
          </button>
          {Object.keys(catList).map((cat) => (
            <button
              key={cat}
              className={`cat ${
                !filter.page && filter.cat?.toLowerCase() === cat.toLowerCase()
                  ? "active"
                  : ""
              }`}
              onClick={() => {
                setFilter({ ...filter, page: "", tag: "", name: "", cat });
                if (listRef.current && filter.cat) {
                  listRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }
              }}
            >
              {cat} ({catList[cat]})
            </button>
          ))}
        </div>
      </div>
    );
  }

  function renderApps(appsList: ICatalogAppEs[]) {
    if (pluginList.length === 0) {
      return <div className="apps-container">Cargando...</div>;
    }

    return (
      <div ref={listRef} className={`apps-container`}>
        {appsList.length > 0 && (
          <>
            {!filter.name && !filter.cat && !filter.tag && (
              <Link
                title="Embeddable: creador de widgets con IA"
                className="app embd-promo"
                href="https://embeddable.co?ref=commonninja"
                target="_blank"
              >
                <div className="new-ribbon">NUEVO</div>
                <Image
                  src="/assets/embeddable-banner.png"
                  alt="Embeddable: creador de widgets con IA"
                  width={320}
                  height={170}
                />
                <div className="details">
                  <h3>
                    Creador de widgets con IA
                    <EnTag />
                  </h3>
                  <p>
                    Crea cualquier widget personalizado que imagines con el
                    poder de la IA. Sin código: describe lo que necesitas y
                    míralo cobrar vida.
                  </p>
                </div>
              </Link>
            )}
            {appsList.map((plugin: ICatalogAppEs) => {
              return (
                <Link
                  title={`${plugin.name} para ${platform.title}`}
                  key={plugin.slug}
                  className="app"
                  href={plugin.href}
                  hrefLang={plugin.isEn ? "en" : "es"}
                >
                  <Image
                    src={plugin.catalogImage}
                    alt={`Logo de ${plugin.name} para ${platform.title}`}
                    width={320}
                    height={170}
                  />
                  <div className="details">
                    <h3>
                      {plugin.name} para {platform.title}
                      {plugin.isEn && <EnTag />}
                    </h3>
                    <p>{plugin.description || plugin.teaser}</p>
                  </div>
                </Link>
              );
            })}
          </>
        )}
        {!appsList.length && (
          <div className="no-items embd-promo">
            <div className="promo-banner">
              <div className="promo-icon">
                <Image
                  src="/assets/icon.png"
                  alt="Ícono de Common Ninja"
                  width={100}
                  height={100}
                />
              </div>
            </div>
            <div className="promo-content">
              <h2>¿No encontraste lo que buscabas?</h2>
              <h3>Prueba Embeddable, nuestro creador de widgets con IA</h3>
              <p>
                Crea cualquier widget personalizado que imagines con el poder
                de la IA. Sin código: describe lo que necesitas y míralo cobrar
                vida.
              </p>
              <div className="promo-features">
                <span>🎨 Totalmente personalizable</span>
                <span>⚡ Creación instantánea</span>
                <span>🚀 Integración sencilla</span>
              </div>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  window.open("https://embeddable.co?ref=commonninja");
                }}
                style={{
                  fontSize: 16,
                  padding: "14px 32px",
                }}
              >
                Prueba Embeddable ahora →
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderHome() {
    return (
      <div className="widget-catalog-home">
        {tagList.map((tag) => {
          return (
            <div className="home-section" key={tag}>
              <h3>
                {tag.toLowerCase().includes("popular") && (
                  <AiFillStar className="icon" />
                )}
                {tag.toLowerCase().includes("trending") && (
                  <AiFillFire className="icon" />
                )}
                {tag.toLowerCase().includes("new") && (
                  <RiLeafFill className="icon" />
                )}
                {TAG_ES[tag] || tag}
              </h3>
              {renderApps(
                pluginList.filter((p) =>
                  (p.newTags || []).map((t) => t.name).includes(tag)
                )
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="inner">
      <div className="main-content-wrapper">
        {renderSidebar()}
        {filter.page === "home" ? renderHome() : renderApps(filteredPlugins)}
      </div>
    </div>
  );
}
